import moment from "moment";
import { FormikProps } from "formik";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { FormInitialValues } from "./PanVendorDataFilterCardWrapper";

type Props = {
  formikProps: FormikProps<FormInitialValues>;
  onReset: () => void;
  /** Earliest pickable date — set for sub-admins only, see the wrapper. */
  minDate?: string;
};

/**
 * The application statuses a generated form can be sitting at. A form is only
 * generated from FORM_FILL_IN_PROGRESS onwards, and the application can move on
 * (or be rejected / cancelled) afterwards without the form going anywhere.
 */
const STATUS_OPTIONS = [
  { label: "Form Fill In Progress", value: "FORM_FILL_IN_PROGRESS" },
  { label: "Verify", value: "VERIFY" },
  { label: "Generate", value: "GENERATE" },
  { label: "Done", value: "DONE" },
  { label: "Reject", value: "REJECT" },
  { label: "Cancelled", value: "CANCELLED" },
];

const TYPE_OPTIONS = [
  { label: "New", value: "NEW" },
  { label: "Correction", value: "CORRECTION" },
];

/**
 * The centres an acknowledgement number can be issued against — the first
 * digits of the number itself, which is why this filter is a prefix match on
 * `acknowledgementNumber` rather than a field of its own. 33470 is our own
 * centre (the same prefix print routing keys on, `SJBT_ACK_NUMBER_PREFIX`);
 * anything logged at the other centre carries 9103642.
 *
 * The vendor sheet is uploaded per centre, so this is what decides which rows
 * an export covers.
 */
const REQ_BRANCH_CODE_OPTIONS = [
  { label: "33470", value: "33470" },
  { label: "9103642", value: "9103642" },
];

const PanVendorDataFilterCard = ({ formikProps, onReset, minDate }: Props) => {
  const { values, setFieldValue, isSubmitting } = formikProps;

  return (
    <div className="flex flex-col gap-3 w-[380px]">
      <div className="flex justify-between">
        <div className="text-primary-main font-medium text-[18px]">Filters</div>
        <button
          type="button"
          className="text-sm text-secondary-main font-medium"
          onClick={onReset}
        >
          Reset Filters
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {/* First, because the sheet is cut one centre at a time. */}
        <div>
          <ATMSelect
            name="reqBranchCode"
            value={values.reqBranchCode}
            onChange={(e) => setFieldValue("reqBranchCode", e.target.value)}
            label="Req. Branch Code"
            options={REQ_BRANCH_CODE_OPTIONS}
          />
          {/* The API takes one search term, and this filter spends it — see
              `effectiveSearchValue` in PanVendorDataListingWrapper. Said out
              loud here so an operator whose typed search stops applying can
              see why. */}
          {values.reqBranchCode ? (
            <div className="text-[11px] text-slate-500 pt-1">
              Matches applications whose Acknowledgement No. starts with{" "}
              {values.reqBranchCode}. While selected, it replaces the search box.
            </div>
          ) : null}
        </div>
        <ATMSelect
          name="status"
          value={values.status}
          onChange={(e) => setFieldValue("status", e.target.value)}
          label="Status"
          options={STATUS_OPTIONS}
        />
        <ATMSelect
          name="applicationType"
          value={values.applicationType}
          onChange={(e) => setFieldValue("applicationType", e.target.value)}
          label="Type"
          options={TYPE_OPTIONS}
        />
      </div>

      {/* Generated-date range — the date the PAN form was generated, i.e. the
          batch the vendor file is being cut for, not the applied date. */}
      <div className="grid lg:grid-cols-2 xs:grid-cols-1 gap-2">
        <div>
          <ATMDatePicker
            name="startDate"
            value={values.startDate}
            onChange={(newValue) => setFieldValue("startDate", newValue)}
            label="Generated From"
            minDate={minDate}
            maxDate={moment().format("yyyy-MM-DD")}
          />
        </div>
        <div>
          <ATMDatePicker
            name="endDate"
            value={values.endDate}
            onChange={(newValue) => setFieldValue("endDate", newValue)}
            label="Generated To"
            minDate={values?.startDate || minDate}
            maxDate={moment().format("yyyy-MM-DD")}
          />
        </div>
      </div>

      <div className="mt-6">
        <ATMLoadingButton
          type="submit"
          isLoading={isSubmitting}
          className="bg-primary-main"
        >
          Apply
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default PanVendorDataFilterCard;
